
import { get, first } from 'lodash';
import axios from '../services/axios';
import { failureUri, thankYouUri } from '../config/uri';
import { HINTS_URL, RECEIPT_SERVICE_URL, RESTAURANT_ID } from '../config/api';
import { authHeader, getValidToken } from '../services/getValidToken';
import { isKioskCustomerSelector } from '../selectors/userSelectors';
import { validateUserDocs } from './user';
import { hideModal, openModal } from '../reducers/modalReducer';
import { getOrder, resetCart } from '../reducers/cartReducer';
import { checkoutFinish, checkoutStart } from '../reducers/uploadReducer';
import { getFullName } from '../reducers/userReducer';
import { sliceRewardPoints } from '../helpers/productHelper';
import { modalPropsSelector } from '../selectors/modalSelectors';
import {
  isKioskMode as isKioskSelector,
} from '../selectors/viewSelectors';
import { kioskModeType } from '../config/const';
import {
  isPaymentKiosk as isPaymentKioskSelector,
  getCurrentTicketType,
} from '../selectors/configSelectors';
import { fromObjToStringFields } from '../helpers/strings';
import { placeOrderReset } from '../reducers/checkoutReducer';
import { addPaymentAndGetStatus, updatePayment, getPaymentSummary, PAYMENT_TYPE } from './pots-api';
import { setFetching, clearSummary } from '../reducers/summaryReducer';
import { getCashHandlingUsers, getCashHandlingAccounts } from '../services/paymentKiosk';
import {
  CASH_HANDLING_UNTRACKED_ACCOUNT,
  CASH_HANDLING_CASH_ACCOUNT_SUFFIX,
  CASH_HANDLING_CREDIT_CARD_ACCOUNT_SUFFIX,
} from '../config/paymentKiosk';
import { sendPageViewEvent, sendPurchaseEvent } from '../services/conversions';
import { pushWithQueryParams } from '../services/uri';

const CURRENCY_TYPE = {
  CASH: 'CASH',
  CREDIT_CARD: 'CREDIT_CARD'
};

const TRANSACTION_TYPE = {
  TRANSFER: 'TRANSFER'
};

const PAYMENT_SOURCE = 'kiosk';
const PAYMENT_METHOD = 'cash';

export const showSignUpCancelledModal = () => (dispatch) => {
  dispatch(openModal({
    modalType: 'SIGNUP_CANCELLED',
    modalProps: {}
  }));
}

export const showOrdersNotAllowedModal = (ticketResult) => (dispatch) => {
  dispatch(
    openModal({
      modalType: 'GENERIC',
      modalProps: { smallText: ticketResult.eta || ticketResult },
    }),
  );
}

export const showProductOutOfStockModal = (ticketResult, orderStatus, strictMedCustomerWithRecItems) => (dispatch) => {
  const products = ticketResult.unavailableItems;
  dispatch(
    openModal({
      modalType: 'CHECKOUT_ERROR',
      modalProps: {
        products,
        orderStatus: orderStatus,
        orderId: ticketResult.orderId,
        eta: ticketResult.eta,
        strictMedCustomerWithRecItems
      }
    })
  );
}

export const showCustomerAgeTooHighModal = () => (dispatch) => {
  dispatch(openModal({
    modalType: 'GENERIC',
    modalProps: {
        text: 'Date of Birth is likely set incorrectly.',
        smallText: 'Please correct it or contact support.'
    }
  }));
}

export const saveTicket = (data, potsTid = null, kioskSettings = null) => async (dispatch, getState) => {
  const state = getState();
  const isKioskCustomer = isKioskCustomerSelector(state);
  const isKioskMode = isKioskSelector(state);
  const isPaymentKioskMode = isPaymentKioskSelector(state);
  const order = getOrder(state);
  const { notes = '' } = data;
  let { rewardDollars } = data;

  const ticketType = getCurrentTicketType(state);

  if (!isKioskMode && dispatch(validateUserDocs())) {
    dispatch(
      openModal({
        modalType: 'GENERIC',
        modalProps: { text: 'Please Upload Images to Checkout', stay: true }
      })
    );
  }

  const items = order.items.map(item => ({ 
    createdAt: item.createdAt,
    productId: item.productId,
    quantity: item.quantity,
    customerType: item.customerType,
    terminalId: item.terminalId,
  }));
  rewardDollars = Number(sliceRewardPoints(rewardDollars));

  dispatch(checkoutStart());

  const ticket = {
    type: ticketType,
    items,
    rewardDollars,
    notes,
  };

  let kioskTicketUrl;

  const hintsServiceUrl = `${HINTS_URL}/HintsService/v1.0/rest/ecommerce/`;

  if (isKioskMode) {
    kioskTicketUrl = isKioskCustomer ? `${hintsServiceUrl}kioskTicketNoCustomer` : `${hintsServiceUrl}kioskTicketWithCustomer`;
  } else {
    kioskTicketUrl = `${hintsServiceUrl}ticket`;
  }

  let kioskTicketData;

  const authToken = await getValidToken();

  return axios.post(kioskTicketUrl, ticket, authHeader(authToken))
    .then((postResult) => {
      kioskTicketData = postResult.data;
    })
    .then(async () => {
      if (!isPaymentKioskMode || kioskTicketData.resultCode !== 'SUCCESS') {
        return Promise.resolve();
      }

      const { orderId, orderNumber } = kioskTicketData; // saved sale

      const customerName = getFullName(state);

      const amountPaid = get(state, 'summary.summary.ticketTotals.total', 0); // ticketcalc pojo

      return await processKioskPayment(orderId, orderNumber, customerName, amountPaid, potsTid);
    })
    .then(() => {
      const isSuccess = kioskTicketData.resultCode === 'SUCCESS';
     
      dispatch(reset());
      
      if (kioskTicketData.resultCode === 'FAIL' && kioskTicketData.resultReason === 'SIGN_UP_CANCELED') {
        dispatch(showSignUpCancelledModal());
      } else if (kioskTicketData.resultCode === 'FAIL' && kioskTicketData.resultReason === 'ORDERS_NOT_ALLOWED') {
        dispatch(showOrdersNotAllowedModal(kioskTicketData));
      } else if (kioskTicketData.resultCode === 'FAIL' && kioskTicketData.resultReason === 'CUSTOMER_AGE_TOO_HIGH') {
        dispatch(showCustomerAgeTooHighModal());
      } else if (kioskTicketData.resultCode === 'FAIL' && kioskTicketData.resultReason === 'PRODUCT_OUT_OF_STOCK') {
        dispatch(showProductOutOfStockModal(kioskTicketData, false, false));
      } else if (kioskTicketData.resultCode === 'FAIL' && kioskTicketData.resultReason === 'STRICT_MEDICAL_CUSTOMER_WITH_REC_ITEMS') {
        dispatch(showProductOutOfStockModal(kioskTicketData, false, true));
      } else if (kioskTicketData.unavailableItems.length > 0) {
        dispatch(showProductOutOfStockModal(kioskTicketData, true, false));
      } else if (!isSuccess) {
        dispatch(reset());
        dispatch(pushWithQueryParams(failureUri));
      } else {
        sendPurchaseEvent(kioskTicketData);
        sendPageViewEvent('Thank you page', thankYouUri);
        dispatch(placeOrderReset());
        dispatch(pushWithQueryParams(thankYouUri));
      }
    }).catch((err) => {
      dispatch(reset());
      dispatch(pushWithQueryParams(failureUri));
    });
};

export const saveDraftTicket = (data) => async (dispatch, getState) => {
  const state = getState();
  const order = getOrder(state);
  const { notes = '' } = data;
  let { rewardDollars } = data;

  const ticketType = getCurrentTicketType(state);

  const items = order.items.map(item => ({ 
    createdAt: item.createdAt,
    productId: item.productId,
    quantity: item.quantity,
    customerType: item.customerType,
    terminalId: item.terminalId,
  }));
  rewardDollars = Number(sliceRewardPoints(rewardDollars));

  const ticket = {
    type: ticketType,
    items,
    rewardDollars,
    notes,
  };

  const url = `${HINTS_URL}/HintsService/v1.0/rest/ecommerce/ticket/draft`;

  const authToken = await getValidToken();

  return axios.post(url, ticket, authHeader(authToken))
    .then((result) => {
      const kioskTicketData = result.data;

      const isSuccess = kioskTicketData.resultCode === 'SUCCESS';
      
      if (!isSuccess) {
        dispatch(reset());
      }

      if (kioskTicketData.resultCode === 'FAIL' && kioskTicketData.resultReason === 'SIGN_UP_CANCELED') {
        dispatch(showSignUpCancelledModal());
      } else if (kioskTicketData.resultCode === 'FAIL' && kioskTicketData.resultReason === 'ORDERS_NOT_ALLOWED') {
        dispatch(showOrdersNotAllowedModal(kioskTicketData));
      } else if (kioskTicketData.resultCode === 'FAIL' && kioskTicketData.resultReason === 'CUSTOMER_AGE_TOO_HIGH') {
        dispatch(showCustomerAgeTooHighModal());
      } else if (kioskTicketData.resultCode === 'FAIL' && kioskTicketData.resultReason === 'PRODUCT_OUT_OF_STOCK') {
        dispatch(showProductOutOfStockModal(kioskTicketData, false, false));
      } else if (kioskTicketData.resultCode === 'FAIL' && kioskTicketData.resultReason === 'STRICT_MEDICAL_CUSTOMER_WITH_REC_ITEMS') {
        dispatch(showProductOutOfStockModal(kioskTicketData, false, true));
      } else if (kioskTicketData.unavailableItems.length > 0) {
        dispatch(showProductOutOfStockModal(kioskTicketData, true, false));
      } else if (!isSuccess) {
        dispatch(pushWithQueryParams(failureUri));
      }

      return Promise.resolve(result);
    }).catch((err) => {
      dispatch(reset());
      dispatch(pushWithQueryParams(failureUri));
    });
};

export const abandonDraftTicket =  (ticketId) => async () => {
  const url = `${HINTS_URL}/HintsService/v1.0/rest/ecommerce/ticket/${ticketId}/abandonDraft`;

  const authToken = await getValidToken();

  return axios.post(url, null, authHeader(authToken));
};

export const cancelTicketAndPayment = (ticketId, chargeId) => async () => {
  const url = `${HINTS_URL}/HintsService/v1.0/rest/ecommerce/ticket/${ticketId}/charges/${chargeId}/cancel`;

  const authToken = await getValidToken();

  return axios.post(url, null, authHeader(authToken));
};

export const processDraftTicket = (ticketId, chargeId) => async () => {
  const url = `${HINTS_URL}/HintsService/v1.0/rest/ecommerce/ticket/${ticketId}/charges/${chargeId}/processDraft`;

  const authToken = await getValidToken();

  return axios.post(url, null, authHeader(authToken));
}

export const generateMsoAchPaymentLink = (ticketId) => async () => {

  const url = `${HINTS_URL}/HintsService/v1.0/rest/ecommerce/ticket/${ticketId}/createInvoice`;

  const authToken = await getValidToken();

  return axios.get(url, authHeader(authToken));
}

const reset = () => dispatch => {
  dispatch(resetCart());
  dispatch(clearSummary());
  dispatch(setFetching(false));
  dispatch(checkoutFinish());
};

export const resetCheckout = () => dispatch => {
  dispatch(reset());
  dispatch(hideModal());
}

export const addPaymentAndWaitStatus = (amount) => async (dispatch, getState) => addPaymentAndGetStatus(amount);

export const saveTicketFromKioskOrderModal = (formData) => (dispatch, getState) => {
  const state = getState();
  const modalProps = modalPropsSelector(state);

  const notes = fromObjToStringFields(formData);
  const { isFromCheckoutPage } = modalProps;

  if (isFromCheckoutPage) {
    dispatch(hideModal());
    return dispatch(saveTicket({ notes }));
  }

  return console.warn('Incorrect "KioskModal" submit handler');
};

export const saveTicketFromPaymentKioskOrderModal = (formData, potsTid, kioskSettings) => (dispatch, getState) => {
  dispatch(hideModal());

  const notes = fromObjToStringFields(formData);

  return dispatch(saveTicket(notes ? { notes } : {}, potsTid, kioskSettings));
};

export async function payTicketAndReportCashFlow(orderId, orderNumber, amountPaid, potsTid, customerName, paidBy) {
  const cashFlowData = await generateCashFlowData(orderNumber, customerName, paidBy);

  const updateTicketData = {
    external_order_number: potsTid,
    payments: [
      {
        payment_source: PAYMENT_SOURCE,
        payment_method: PAYMENT_METHOD,
        amount_paid: amountPaid
      }
    ]
  };

  const requestData = {
    updateTicketReq: updateTicketData,
    createTransactionReqs: cashFlowData
  };

  const url = `${HINTS_URL}/HintsService/v1.0/rest/ticket/external/ticket/payAndReportCash/${orderId}`;

  const authToken = await getValidToken();

  return axios.put(url, requestData, authHeader(authToken));
}

export async function generateCashFlowData(orderNumber, customerName, paidBy) {
  if (!Array.isArray(paidBy)) {
    console.warn('generateCashFlowData: PaidBy information is missing');

    return [];
  }

  const availableUsers = await getCashHandlingUsers();

  const transactionCreator = first(availableUsers) || null;

  if (transactionCreator === null) {
    console.warn('generateCashFlowData: Cannot find transactionCreator');

    return [];
  }

  const availableAccounts = await getCashHandlingAccounts();

  //TODO finding accounts by name for BETA release only, should be updated once full cash drawer integration is complete
  const fromAccount = availableAccounts.find(a => a.name === CASH_HANDLING_UNTRACKED_ACCOUNT) || null;

  if (fromAccount === null) {
    console.warn('generateCashFlowData: Cannot find fromAccount');

    return [];
  }

  const orderNotes = `SALE - Ticket ${orderNumber} - ${customerName}`;

  let accountsMissing = false;

  let requestData = [];

  paidBy.forEach(currentPaidBy => {
    let toAccount = null;
    let currencyType = null;

    switch (currentPaidBy.method) {
      case PAYMENT_TYPE.COUPON:
      case PAYMENT_TYPE.CASH:
        //TODO for BETA only, needs to be updated/removed after cash drawer integration
        toAccount = availableAccounts.find(a => a.name.endsWith(CASH_HANDLING_CASH_ACCOUNT_SUFFIX)) || null;
        currencyType = CURRENCY_TYPE.CASH;
        break;

      case PAYMENT_TYPE.CARD:
        //TODO for BETA only, needs to be updated/removed after cash drawer integration
        toAccount = availableAccounts.find(a => a.name.endsWith(CASH_HANDLING_CREDIT_CARD_ACCOUNT_SUFFIX)) || null;
        currencyType = CURRENCY_TYPE.CREDIT_CARD;
        break;

      default:
        break;
    }

    if (toAccount === null || currencyType === null) {
      console.warn('generateCashFlowData: Cannot find toAccount or currencyType');

      accountsMissing = true;

      return;
    }

    const amountPaid = currentPaidBy.amount;

    requestData.push({
      fromAccountId: fromAccount.id,
      toAccountId: toAccount.id,
      amount: amountPaid,
      transactionType: TRANSACTION_TYPE.TRANSFER,
      currencyType: currencyType,
      notes: orderNotes,
      restaurantId: RESTAURANT_ID,
      creatorId: transactionCreator.id
    });
  });

  return accountsMissing ? [] : requestData;
}

export async function getReceipt(orderId, retriesLeft = 5, retryDelay = 1000) {
  const receiptUrl = `${RECEIPT_SERVICE_URL}/ticket/${orderId}/type/receipt`;

  const authToken = await getValidToken();

  return new Promise((resolve, reject) => {
    // Skip default interceptor
    axios
      .create()
      .get(receiptUrl, authHeader(authToken))
      .then((receiptResult) => {
        resolve({
          data: {
            receiptData: receiptResult && receiptResult.data ? receiptResult.data : '',
            receiptError: null
          }
        });
      })
      .catch((error) => {
        setTimeout(() => {
          if (retriesLeft === 1) {
            console.warn(error);

            resolve({
              data: {
                receiptData: '',
                receiptError: error && error.message ? error.message : '(Unknown)'
              }
            });

            return;
          }

          getReceipt(orderId, --retriesLeft)
            .then(resolve, reject);
        }, retryDelay);
      });
  });
}

export async function processKioskPayment(orderId, orderNumber, customerName, amountPaid, potsTid) {
  const paymentSummaryResult = await getPaymentSummary(potsTid);

  const paidBy = get(paymentSummaryResult, 'data.paidBy', null);

  const payTicketAndUpdateCashFlowResult = await payTicketAndReportCashFlow(orderId, orderNumber,
    amountPaid, potsTid, customerName, paidBy);

  const externalData = get(payTicketAndUpdateCashFlowResult, 'data.data', null);

  const getReceiptResult = await getReceipt(orderId);

  const paymentData = {
    RequestorTid: orderId,
    ExternalData: JSON.stringify(externalData),
    Receipt: getReceiptResult.data.receiptData,
    ReceiptError: getReceiptResult.data.receiptError,
  };

  return await updatePayment(potsTid, paymentData);
}



// WEBPACK FOOTER //
// ./src/actions/ticket.js